// Client Subscription Service
// Handles subscription tiers, usage limits, and upgrade flows for clients

import { supabase } from "../config/supabaseClient";
import { clientVinService } from "./clientVinService";
import { logger } from "../Utils/ErrorHandler";
/**
 * Subscription Tier Definitions
 * Mirror of database values for client-side reference
 */
export const SUBSCRIPTION_TIERS = {
  free: {
    id: "free",
    name: "Free",
    price: 0,
    priceYearly: 0,
    priceDisplay: "$0/mo",
    // Multi-currency pricing (free in all currencies)
    pricing: {
      USD: { monthly: 0, yearly: 0 },
      CAD: { monthly: 0, yearly: 0 },
      MXN: { monthly: 0, yearly: 0 },
    },
    limits: {
      ocrScansPerMonth: 2,
      tripsPerMonth: 20,
      tripHistoryDays: 30,
      receiptStorageMonths: 3,
      maxVehicles: 1,
    },
    features: {
      canExportReports: false,
    },
  },
  plus: {
    id: "plus",
    name: "Plus",
    price: 4.99,
    priceYearly: 53.99,
    priceDisplay: "$4.99/mo",
    // Multi-currency pricing (matches Stripe)
    pricing: {
      USD: { monthly: 3.99, yearly: 39.99 },
      CAD: { monthly: 4.99, yearly: 53.99 },
      MXN: { monthly: 70, yearly: 700 },
    },
    limits: {
      ocrScansPerMonth: 8,
      tripsPerMonth: null, // unlimited
      tripHistoryDays: 365,
      receiptStorageMonths: 13,
      maxVehicles: 2,
    },
    features: {
      canExportReports: true,
    },
  },
  pro: {
    id: "pro",
    name: "Pro",
    price: 14.99,
    priceYearly: 161.99,
    priceDisplay: "$14.99/mo",
    // Multi-currency pricing (matches Stripe)
    pricing: {
      USD: { monthly: 10.99, yearly: 109.99 },
      CAD: { monthly: 14.99, yearly: 161.99 },
      MXN: { monthly: 190, yearly: 1900 },
    },
    limits: {
      ocrScansPerMonth: 25,
      tripsPerMonth: null, // unlimited
      tripHistoryDays: 730, // 2 years
      receiptStorageMonths: 24,
      maxVehicles: null, // unlimited
    },
    features: {
      canExportReports: true,
    },
  },
};

/**
 * Helper to check if error is due to missing table
 */
const isTableMissingError = (error) => {
  if (!error) return false;
  return (
    error.code === "42P01" ||
    error.code === "PGRST205" ||
    error.message?.includes("does not exist") ||
    error.message?.includes("Could not find")
  );
};

/**
 * Client Subscription Service
 */
export const clientSubscriptionService = {
  // =====================================================
  // SUBSCRIPTION OPERATIONS
  // =====================================================

  /**
   * Get current client's subscription with limits
   */
  getSubscription: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("get_client_subscription", {
        p_client_id: clientResult.clientId,
      });

      if (error) {
        if (isTableMissingError(error)) {
          // Return free tier defaults if tables don't exist yet
          return {
            success: true,
            data: {
              clientId: clientResult.clientId,
              tier: "free",
              status: "active",
              ...SUBSCRIPTION_TIERS.free.limits,
              ...SUBSCRIPTION_TIERS.free.features,
            },
          };
        }
        logger.error("Error fetching subscription:", error);
        return { success: false, error: error.message };
      }

      // Handle case where function returns empty result
      if (!data || data.length === 0) {
        return {
          success: true,
          data: {
            clientId: clientResult.clientId,
            tier: "free",
            status: "active",
            ...SUBSCRIPTION_TIERS.free.limits,
            ...SUBSCRIPTION_TIERS.free.features,
          },
        };
      }

      const subscription = Array.isArray(data) ? data[0] : data;

      return {
        success: true,
        data: {
          clientId: clientResult.clientId,
          tier: subscription.tier || "free",
          status: subscription.status || "active",
          currentPeriodEnd: subscription.current_period_end,
          ocrScansPerMonth: subscription.ocr_scans_per_month,
          tripsPerMonth: subscription.trips_per_month,
          tripHistoryDays: subscription.trip_history_days,
          receiptStorageMonths: subscription.receipt_storage_months,
          maxVehicles: subscription.max_vehicles,
          canExportReports: subscription.can_export_reports,
          hasCraRates: subscription.has_cra_rates,
          hasTaxSummary: subscription.has_tax_summary,
          priceMonthly: subscription.price_monthly_cad,
        },
      };
    } catch (error) {
      logger.error("Error in getSubscription:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get current month's usage for the client
   */
  getUsage: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("get_client_usage", {
        p_client_id: clientResult.clientId,
      });

      if (error) {
        if (isTableMissingError(error)) {
          return {
            success: true,
            data: {
              ocrScansUsed: 0,
              tripsStarted: 0,
              receiptsStored: 0,
              monthYear: new Date().toISOString().slice(0, 7),
            },
          };
        }
        logger.error("Error fetching usage:", error);
        return { success: false, error: error.message };
      }

      const usage = Array.isArray(data) ? data[0] : data;

      return {
        success: true,
        data: {
          ocrScansUsed: usage?.ocr_scans_used || 0,
          tripsStarted: usage?.trips_started || 0,
          receiptsStored: usage?.receipts_stored || 0,
          monthYear: usage?.month_year || new Date().toISOString().slice(0, 7),
        },
      };
    } catch (error) {
      logger.error("Error in getUsage:", error);
      return { success: false, error: error.message };
    }
  },

  // =====================================================
  // LIMIT CHECK OPERATIONS
  // =====================================================

  /**
   * Check if client can perform OCR scan
   * Returns limit status and remaining scans
   */
  checkOcrLimit: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("check_ocr_limit", {
        p_client_id: clientResult.clientId,
      });

      if (error) {
        if (isTableMissingError(error)) {
          // Allow scans if tables don't exist (feature not fully deployed)
          return {
            success: true,
            data: {
              canScan: true,
              scansUsed: 0,
              scansLimit: 4,
              scansRemaining: 4,
              tier: "free",
            },
          };
        }
        logger.error("Error checking OCR limit:", error);
        return { success: false, error: error.message };
      }

      const result = Array.isArray(data) ? data[0] : data;

      return {
        success: true,
        data: {
          canScan: result?.can_scan ?? true,
          scansUsed: result?.scans_used || 0,
          scansLimit: result?.scans_limit || 4,
          scansRemaining: result?.scans_remaining ?? 4,
          tier: result?.tier || "free",
        },
      };
    } catch (error) {
      logger.error("Error in checkOcrLimit:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if client can start a new trip
   * Returns limit status and remaining trips
   */
  checkTripLimit: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("check_trip_limit", {
        p_client_id: clientResult.clientId,
      });

      if (error) {
        if (isTableMissingError(error)) {
          return {
            success: true,
            data: {
              canStartTrip: true,
              tripsUsed: 0,
              tripsLimit: 20,
              tripsRemaining: 20,
              tier: "free",
            },
          };
        }
        logger.error("Error checking trip limit:", error);
        return { success: false, error: error.message };
      }

      const result = Array.isArray(data) ? data[0] : data;

      return {
        success: true,
        data: {
          canStartTrip: result?.can_start_trip ?? true,
          tripsUsed: result?.trips_used || 0,
          tripsLimit: result?.trips_limit, // null = unlimited
          tripsRemaining: result?.trips_remaining, // null = unlimited
          tier: result?.tier || "free",
        },
      };
    } catch (error) {
      logger.error("Error in checkTripLimit:", error);
      return { success: false, error: error.message };
    }
  },

  // =====================================================
  // USAGE INCREMENT OPERATIONS
  // =====================================================

  /**
   * Increment OCR scan usage after successful scan
   * @param {string} provider - 'cloud_vision' or 'tesseract'
   */
  incrementOcrUsage: async (provider = "cloud_vision") => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("increment_client_ocr_usage", {
        p_client_id: clientResult.clientId,
        p_provider: provider,
      });

      if (error) {
        if (isTableMissingError(error)) {
          // Silently succeed if tables don't exist
          return { success: true, newCount: 1 };
        }
        logger.error("Error incrementing OCR usage:", error);
        return { success: false, error: error.message };
      }

      return { success: true, newCount: data };
    } catch (error) {
      logger.error("Error in incrementOcrUsage:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Increment trip usage when starting a new trip
   */
  incrementTripUsage: async () => {
    try {
      const clientResult = await clientVinService.getClientId();
      if (!clientResult.success) {
        return { success: false, error: clientResult.error };
      }

      const { data, error } = await supabase.rpc("increment_client_trip_usage", {
        p_client_id: clientResult.clientId,
      });

      if (error) {
        if (isTableMissingError(error)) {
          return { success: true, newCount: 1 };
        }
        logger.error("Error incrementing trip usage:", error);
        return { success: false, error: error.message };
      }

      return { success: true, newCount: data };
    } catch (error) {
      logger.error("Error in incrementTripUsage:", error);
      return { success: false, error: error.message };
    }
  },

  // =====================================================
  // TIER INFORMATION
  // =====================================================

  /**
   * Get all available subscription tiers
   */
  getTiers: async () => {
    try {
      const { data, error } = await supabase
        .from("subscription_tier_limits")
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) {
        if (isTableMissingError(error)) {
          // Return hardcoded tiers if table doesn't exist
          return {
            success: true,
            data: Object.values(SUBSCRIPTION_TIERS),
          };
        }
        logger.error("Error fetching tiers:", error);
        return { success: false, error: error.message };
      }

      const tiers = data.map((tier) => ({
        id: tier.tier,
        name: tier.display_name,
        price: parseFloat(tier.price_monthly_cad),
        priceDisplay: `$${tier.price_monthly_cad}/mo`,
        limits: {
          ocrScansPerMonth: tier.ocr_scans_per_month,
          tripsPerMonth: tier.trips_per_month,
          tripHistoryDays: tier.trip_history_days,
          receiptStorageMonths: tier.receipt_storage_months,
          maxVehicles: tier.max_vehicles,
        },
        features: {
          canExportReports: tier.can_export_reports,
          hasCraRates: tier.has_cra_rates,
          hasTaxSummary: tier.has_tax_summary,
        },
      }));

      return { success: true, data: tiers };
    } catch (error) {
      logger.error("Error in getTiers:", error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get tier info by ID (client-side, no API call)
   */
  getTierInfo: (tierId) => {
    return SUBSCRIPTION_TIERS[tierId] || SUBSCRIPTION_TIERS.free;
  },

  /**
   * Get upgrade recommendation based on current usage
   */
  getUpgradeRecommendation: async () => {
    try {
      const [subResult, usageResult] = await Promise.all([
        clientSubscriptionService.getSubscription(),
        clientSubscriptionService.getUsage(),
      ]);

      if (!subResult.success || !usageResult.success) {
        return { success: false, error: "Failed to fetch subscription data" };
      }

      const { tier, ocrScansPerMonth, tripsPerMonth } = subResult.data;
      const { ocrScansUsed, tripsStarted } = usageResult.data;

      // Calculate usage percentages
      const ocrUsagePercent = (ocrScansUsed / ocrScansPerMonth) * 100;
      const tripUsagePercent = tripsPerMonth
        ? (tripsStarted / tripsPerMonth) * 100
        : 0;

      let recommendation = null;

      if (tier === "free") {
        if (ocrUsagePercent >= 75 || tripUsagePercent >= 75) {
          recommendation = {
            recommendedTier: "plus",
            reason:
              ocrUsagePercent >= 75
                ? "You're approaching your OCR scan limit"
                : "You're approaching your trip limit",
            urgency: ocrUsagePercent >= 100 || tripUsagePercent >= 100 ? "high" : "medium",
          };
        }
      } else if (tier === "plus") {
        if (ocrUsagePercent >= 80) {
          recommendation = {
            recommendedTier: "pro",
            reason: "You're frequently using OCR scans",
            urgency: ocrUsagePercent >= 100 ? "high" : "medium",
          };
        }
      }

      return {
        success: true,
        data: {
          currentTier: tier,
          usage: {
            ocrScansUsed,
            ocrScansLimit: ocrScansPerMonth,
            ocrUsagePercent: Math.round(ocrUsagePercent),
            tripsStarted,
            tripsLimit: tripsPerMonth,
            tripUsagePercent: Math.round(tripUsagePercent),
          },
          recommendation,
        },
      };
    } catch (error) {
      logger.error("Error in getUpgradeRecommendation:", error);
      return { success: false, error: error.message };
    }
  },
};

export default clientSubscriptionService;
